import { useCallback, useEffect, useMemo, useState } from "react";

import type { LegacyMatch } from "../api/legacyTypes";
import { usePreferences } from "../app/preferencesContext";
import { isUnseenResult, readResultHistory, resultHistoryStorageKey, resultSnapshot, writeResultHistory, type ResultMap } from "../utils/resultHistory";

export function useTournamentResults(tournamentId: string, matches: LegacyMatch[]) {
  const [previous, setPrevious] = useState<ResultMap | null>(() => readResultHistory(tournamentId));
  const { antiSpoiler } = usePreferences();
  const current = useMemo(() => resultSnapshot(matches), [matches]);
  useEffect(() => {
    const resultListener = (event: Event) => {
      if ((event as CustomEvent<string>).detail === tournamentId) setPrevious(readResultHistory(tournamentId));
    };
    const storageListener = (event: StorageEvent) => {
      if (event.key === resultHistoryStorageKey(tournamentId)) setPrevious(readResultHistory(tournamentId));
    };
    window.addEventListener("fp:results-changed", resultListener);
    window.addEventListener("storage", storageListener);
    return () => {
      window.removeEventListener("fp:results-changed", resultListener);
      window.removeEventListener("storage", storageListener);
    };
  }, [tournamentId]);
  const write = useCallback((results: ResultMap) => {
    setPrevious(results);
    writeResultHistory(tournamentId, results);
  }, [tournamentId]);
  const isNew = useCallback((match: LegacyMatch) => isUnseenResult(previous, match), [previous]);
  const reveal = useCallback((match: LegacyMatch) => write({ ...(previous ?? {}), [match.sourceKey]: match.resultSignature }), [previous, write]);
  const revealAll = useCallback(() => write(current), [current, write]);
  const initialize = useCallback(() => { if (previous === null || !antiSpoiler) write(current); }, [antiSpoiler, current, previous, write]);
  return { antiSpoiler, previous, current, isNew, hidden: (match: LegacyMatch) => antiSpoiler && isNew(match), reveal, revealAll, initialize, newCount: matches.filter(isNew).length };
}
